// Pictures Catalog — mirror of Videos catalog for photo content tracking
// Tracks publication status across OF VIP / JFF / RFC / Faphouse / OF Free / DarkFans / Top4Fans
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import CloudinaryPicker from "../components/CloudinaryPicker";
import Content360Panel from "../components/Content360Panel";
import GalleryImage from "../components/GalleryImage";
import { Plus, Trash2, Pencil, X as XIcon, Image as ImageIcon, Camera, Tag, Filter, Grid as GridIcon, List as ListIcon, ChevronRight, Folder, FolderPlus, ArrowUp, ArrowDown, Film, PlayCircle, Clock } from "lucide-react";
import GalleryClipUploadModal from "../components/GalleryClipUploadModal";
import GalleryMediaViewer from "../components/GalleryMediaViewer";

const PPV_OPTIONS = ["none", "presale", "sale", "live_sub", "scheduled", "posted"];
const EDIT_OPTIONS = ["pending", "edit_now", "semi_edited", "edited", "coming_soon", "free_promo"];
const TIERS = [
  { key: "free", label: "Free", store: "free", tone: "cyan" },
  { key: "basic", label: "Basic", store: "members", tone: "emerald" },
  { key: "vip", label: "VIP", store: "vip", tone: "amber" },
];
const PLATFORMS = [
  { key: "of_vip",   label: "OF VIP",   priced: true },
  { key: "of_free",  label: "OF Free",  priced: false },
  { key: "jff",      label: "JFF",      priced: true },
  { key: "rfc",      label: "RFC",      priced: true },
  { key: "faphouse", label: "Faphouse", priced: true },
  { key: "top4fans", label: "Top4Fans", priced: true },
  { key: "darkfans", label: "DarkFans", priced: true },
];

export default function Pictures() {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [folderEditor, setFolderEditor] = useState(null);
  const [clipUploadOpen, setClipUploadOpen] = useState(false);
  const [previewPicture, setPreviewPicture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("grid"); // grid | table
  const [filter, setFilter] = useState("all"); // all | ready | pending | published
  const [search, setSearch] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pageDragOver, setPageDragOver] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkTier, setBulkTier] = useState("free");
  const [bulkFolderId, setBulkFolderId] = useState("");
  const [bulkTag, setBulkTag] = useState("");
  const [crmContacts, setCrmContacts] = useState([]);
  const [creatorFilter, setCreatorFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    api.get("/api/crm/?limit=500").then(r => setCrmContacts(r.data || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [picturesResponse, foldersResponse] = await Promise.all([
        api.get("/api/pictures/"),
        api.get("/api/pictures/folders/"),
      ]);
      setItems(picturesResponse.data || []);
      setFolders(foldersResponse.data || []);
      setLoadError("");
    } catch (e) { setLoadError(e?.response?.data?.detail || "Could not load pictures"); toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setOpen(true); };
  const onBulkPicked = async (assets) => {
    if (!assets?.length) return;
    setBulkBusy(true);
    setUploadProgress({ complete: 0, total: assets.length, failed: 0 });
    let ok = 0, fail = 0;
    for (const a of assets) {
      try {
        await api.post("/api/pictures/", {
          title: "",
          image_url: a.secure_url,
          image_public_id: a.public_id,
          folder_id: activeFolderId,
          sort_order: folderPictures.length + ok,
          edit_status: "pending",
        });
        ok++;
      } catch { fail++; }
      setUploadProgress({ complete: ok + fail, total: assets.length, failed: fail });
    }
    setBulkBusy(false);
    setBulkOpen(false);
    toast.success(`📸 ${ok} picture${ok === 1 ? "" : "s"} added${fail ? ` · ${fail} failed` : ""} — edit details below`);
    await load();
    setUploadProgress(null);
  };

  // Page-level drag-drop: upload files directly to Cloudinary and create pictures
  const onPageDragOver = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    if (!pageDragOver) setPageDragOver(true);
  };
  const onPageDragLeave = (e) => {
    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
    setPageDragOver(false);
  };
  const onPageDrop = async (e) => {
    e.preventDefault();
    setPageDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []).filter(f => f.type.startsWith("image/"));
    if (!files.length) {
      toast.error("Drop image files only");
      return;
    }
    setBulkBusy(true);
    setUploadProgress({ complete: 0, total: files.length, failed: 0 });
    let ok = 0, fail = 0;
    for (const f of files) {
      if (f.size > 15 * 1024 * 1024) {
        toast.error(`${f.name} > 15 MB`);
        fail++;
        setUploadProgress({ complete: ok + fail, total: files.length, failed: fail });
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("folder", "pictures/uploads");
        fd.append("resource_type", "image");
        const r = await api.post("/api/cloudinary/upload", fd);
        await api.post("/api/pictures/", {
          title: "",
          image_url: r.data.secure_url,
          image_public_id: r.data.public_id,
          folder_id: activeFolderId,
          sort_order: folderPictures.length + ok,
          edit_status: "pending",
        });
        ok++;
      } catch { fail++; }
      setUploadProgress({ complete: ok + fail, total: files.length, failed: fail });
    }
    setBulkBusy(false);
    toast.success(`📸 ${ok}/${files.length} pictures uploaded${fail ? ` · ${fail} failed` : ""}`);
    await load();
    setUploadProgress(null);
  };
  const deleteFolder = async (folder) => {
    if (!window.confirm(`Delete “${folder.title}”? Its pictures and subfolders will be moved to its parent.`)) return;
    try {
      await api.delete(`/api/pictures/folders/${folder.id}`);
      if (activeFolderId === folder.id) setActiveFolderId(folder.parent_id || null);
      toast.success("Folder deleted; contents preserved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Folder could not be deleted"); }
  };
  const movePictureOrder = async (pictureId, direction) => {
    if (search.trim() || filter !== "all" || creatorFilter !== "all" || tagFilter !== "all" || dateFilter) return;
    const ordered = [...folderPictures];
    const index = ordered.findIndex(picture => picture.id === pictureId);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= ordered.length) return;
    [ordered[index], ordered[nextIndex]] = [ordered[nextIndex], ordered[index]];
    try {
      await api.post("/api/pictures/reorder", {
        folder_id: activeFolderId,
        picture_ids: ordered.map(picture => picture.id),
      });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reorder pictures"); }
  };
  const runBulkFolderAction = async (action, value) => {
    if (!selected.size) return toast.error("Nothing selected");
    const set = {};
    const payload = { ids: Array.from(selected), set };
    if (action === "move") set.folder_id = value || null;
    if (action === "publish") set.published = true;
    if (action === "unpublish") set.published = false;
    if (action === "tag") payload.add_tags = [value];
    try {
      if (action === "copy") {
        await api.post("/api/pictures/bulk-copy", { ids: Array.from(selected), folder_id: value || null });
      } else {
        await api.post("/api/pictures/bulk-update", payload);
      }
      toast.success(action === "move" ? "Pictures moved" : action === "copy" ? "Pictures copied as drafts" : action === "tag" ? "Tag added" : `Pictures ${action === "publish" ? "published" : "unpublished"}`);
      setSelected(new Set());
      setSelectMode(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this picture entry?")) return;
    await api.delete(`/api/pictures/${id}`);
    toast.success("Deleted");
    load();
  };
  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = (visibleIds) => {
    setSelected(prev => {
      const allChecked = visibleIds.every(id => prev.has(id));
      const next = new Set(prev);
      if (allChecked) {
        visibleIds.forEach(id => next.delete(id));
      } else {
        visibleIds.forEach(id => next.add(id));
      }
      return next;
    });
  };
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const bulkDelete = async () => {
    if (selected.size === 0) return toast.error("Nothing selected");
    if (!window.confirm(`Delete ${selected.size} picture${selected.size === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const r = await api.post("/api/pictures/bulk-delete", { ids: Array.from(selected) });
      toast.success(`🗑 Deleted ${r.data.deleted_count} pictures`);
      setSelected(new Set());
      setSelectMode(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  };
  const bulkTierChange = async (action) => {
    if (!selected.size) return toast.error("Nothing selected");
    const tiers = bulkTier === "all" ? TIERS.map(t => t.key) : [bulkTier];
    if (action === "remove" && !window.confirm(`Remove ${selected.size} picture(s) from ${bulkTier === "all" ? "all tiers" : TIERS.find(t => t.key === bulkTier).label}?`)) return;
    try {
      const r = await api.post("/api/pictures/bulk/tiers", { ids: Array.from(selected), tiers, action });
      toast.success(`${action === "assign" ? "Assigned" : "Removed"} tiers for ${r.data.modified_count} picture(s)`);
      setSelected(new Set());
      setSelectMode(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${action} picture tiers`);
    }
  };
  const runBulkEdit = async (payload) => {
    if (selected.size === 0) return toast.error("Nothing selected");
    try {
      const r = await api.post("/api/pictures/bulk-update", { ids: Array.from(selected), ...payload });
      toast.success(`✓ Updated ${r.data.modified_count} of ${r.data.matched_count} pictures`);
      setBulkEditOpen(false);
      setSelected(new Set());
      setSelectMode(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk edit failed");
    }
  };
  const patch = async (id, field, val) => {
    try {
      await api.put(`/api/pictures/${id}`, { [field]: val });
      setItems((prev) => prev.map(p => p.id === id ? { ...p, [field]: val } : p));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Picture update failed");
    }
  };
  const togglePictureTier = (pic, tier) => {
    const visibility = [...(pic.visibility || [])];
    const values = tier === "basic" ? ["basic", "members"] : [tier];
    const hasTier = values.some(value => visibility.includes(value));
    const next = visibility.filter(value => !values.includes(value));
    if (!hasTier) next.push(tier === "basic" ? "members" : tier);
    patch(pic.id, "visibility", next);
  };

  const folderPictures = useMemo(() => items
    .filter(picture => (picture.folder_id || null) === activeFolderId)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)), [items, activeFolderId]);
  const childFolders = folders.filter(folder => (folder.parent_id || null) === activeFolderId);
  const folderById = useMemo(() => new Map(folders.map(folder => [folder.id, folder])), [folders]);
  const breadcrumbs = useMemo(() => {
    const trail = [];
    let current = folders.find(folder => folder.id === activeFolderId);
    const seen = new Set();
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      trail.unshift(current);
      current = folders.find(folder => folder.id === current.parent_id);
    }
    return trail;
  }, [folders, activeFolderId]);
  const creatorOptions = useMemo(() => [...new Set([
    ...items.flatMap(picture => picture.crew_names_cache || []),
    ...folders.map(folder => folder.creator_name).filter(Boolean),
  ])].sort(), [items, folders]);
  const tagOptions = useMemo(() => [...new Set([
    ...items.flatMap(picture => picture.tags || []),
    ...folders.flatMap(folder => folder.tags || []),
  ])].sort(), [items, folders]);

  const filtered = useMemo(() => {
    let list = folderPictures;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p => (p.title || "").toLowerCase().includes(q) ||
                               (p.description || "").toLowerCase().includes(q) ||
                               (p.tags || []).some(t => t.toLowerCase().includes(q)) ||
                               (p.crew_names_cache || []).some(name => name.toLowerCase().includes(q)) ||
                               (folderById.get(p.folder_id)?.title || "").toLowerCase().includes(q) ||
                               (folderById.get(p.folder_id)?.creator_name || "").toLowerCase().includes(q));
    }
    if (creatorFilter !== "all") list = list.filter(p => (p.crew_names_cache || []).includes(creatorFilter) || folderById.get(p.folder_id)?.creator_name === creatorFilter);
    if (tagFilter !== "all") list = list.filter(p => (p.tags || []).includes(tagFilter) || (folderById.get(p.folder_id)?.tags || []).includes(tagFilter));
    if (dateFilter) list = list.filter(p => (p.taken_at || "") >= dateFilter);
    if (filter === "ready")   list = list.filter(p => p.edit_status === "edited" || p.edit_status === "free_promo");
    if (filter === "pending") list = list.filter(p => p.edit_status === "pending" || p.edit_status === "edit_now");
    if (filter === "published") list = list.filter(p => p.published);
    if (filter === "unpublished") list = list.filter(p => !p.published);
    return list.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [folderPictures, folderById, filter, search, creatorFilter, tagFilter, dateFilter]);

  return (
    <AppLayout
      title="Pictures"
      overline={`${items.length} PICTURES · ${folders.length} FOLDERS`}
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          {selectMode && (
            <>
              <span className="text-[11px] font-mono-ui font-bold" style={{ color: "var(--gio-primary)" }}
                data-testid="pic-select-count">
                {selected.size} selected
              </span>
              <button onClick={() => selectAllVisible(filtered.map(p => p.id))}
                data-testid="pic-select-all"
                className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border border-[#ff2bd6]/50 text-pink-100 hover:bg-[#ff2bd6]/15">
                <input type="checkbox" checked={filtered.length > 0 && filtered.every(p => selected.has(p.id))} readOnly className="accent-[#ff2bd6]" />
                Select all visible ({filtered.length})
              </button>
              <select value={bulkTier} onChange={e => setBulkTier(e.target.value)} data-testid="pic-bulk-tier" className="gio-input text-[11px] py-1.5">
                {TIERS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                <option value="all">All tiers</option>
              </select>
              <button onClick={() => bulkTierChange("assign")} disabled={!selected.size} data-testid="pic-bulk-tier-assign"
                className="text-[11px] px-3 py-1.5 rounded border border-[#ff2bd6] text-white bg-[#ff2bd6]/20 hover:bg-[#ff2bd6]/40 disabled:opacity-40">Assign</button>
              <button onClick={() => bulkTierChange("remove")} disabled={!selected.size} data-testid="pic-bulk-tier-remove"
                className="text-[11px] px-3 py-1.5 rounded border border-pink-300/60 text-pink-100 hover:bg-pink-500/20 disabled:opacity-40">Remove</button>
              <select value={bulkFolderId} onChange={e => setBulkFolderId(e.target.value)} data-testid="pic-bulk-folder" className="gio-input text-[11px] py-1.5">
                <option value="">Move to root</option>
                {folders.map(folder => <option key={folder.id} value={folder.id}>{folder.title}</option>)}
              </select>
              <button onClick={() => runBulkFolderAction("move", bulkFolderId)} disabled={!selected.size} data-testid="pic-bulk-move"
                className="text-[11px] px-3 py-1.5 rounded border border-cyan-300/60 text-cyan-100 hover:bg-cyan-500/20 disabled:opacity-40">Move</button>
              <button onClick={() => runBulkFolderAction("copy", bulkFolderId)} disabled={!selected.size} data-testid="pic-bulk-copy"
                className="text-[11px] px-3 py-1.5 rounded border border-cyan-300/60 text-cyan-100 hover:bg-cyan-500/20 disabled:opacity-40">Copy</button>
              <input value={bulkTag} onChange={e => setBulkTag(e.target.value)} placeholder="Add tag" aria-label="Add tag to selected pictures"
                data-testid="pic-bulk-tag" className="gio-input text-[11px] py-1.5 w-24" />
              <button onClick={() => { if (bulkTag.trim()) runBulkFolderAction("tag", bulkTag.trim()); }} disabled={!selected.size || !bulkTag.trim()} data-testid="pic-bulk-tag-apply"
                className="text-[11px] px-3 py-1.5 rounded border border-cyan-300/60 text-cyan-100 hover:bg-cyan-500/20 disabled:opacity-40">Tag</button>
              <button onClick={() => runBulkFolderAction("publish")} disabled={!selected.size} data-testid="pic-bulk-publish"
                className="text-[11px] px-3 py-1.5 rounded border border-emerald-300/60 text-emerald-100 hover:bg-emerald-500/20 disabled:opacity-40">Publish</button>
              <button onClick={() => runBulkFolderAction("unpublish")} disabled={!selected.size} data-testid="pic-bulk-unpublish"
                className="text-[11px] px-3 py-1.5 rounded border border-zinc-500 text-zinc-200 hover:bg-white/10 disabled:opacity-40">Unpublish</button>
              <button onClick={() => setBulkEditOpen(true)} disabled={selected.size === 0}
                data-testid="pic-bulk-edit-open"
                className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded border border-[#ff2bd6] text-pink-100 hover:bg-[#ff2bd6]/15 disabled:opacity-40 disabled:cursor-not-allowed">
                <Pencil size={13} /> Bulk edit {selected.size > 0 ? `(${selected.size})` : ""}
              </button>
              <button onClick={bulkDelete} disabled={selected.size === 0}
                data-testid="pic-bulk-delete"
                className="inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded border border-red-600 text-red-500 hover:bg-red-950 disabled:opacity-40 disabled:cursor-not-allowed">
                <Trash2 size={13} /> Delete {selected.size > 0 ? `(${selected.size})` : ""}
              </button>
              <button onClick={() => { setSelectMode(false); setSelected(new Set()); }}
                data-testid="pic-select-cancel"
                className="text-[11px] text-pink-100 hover:text-white">
                Cancel
              </button>
            </>
          )}
          {!selectMode && (
            <>
              <button onClick={() => setSelectMode(true)} data-testid="pic-select-mode"
                className="btn-ghost inline-flex items-center gap-2">
                <ListIcon size={14} /> Select
              </button>
              <button onClick={() => setBulkOpen(true)} data-testid="pic-bulk-button"
                className="btn-ghost inline-flex items-center gap-2" disabled={bulkBusy}>
                <ImageIcon size={16} /> {bulkBusy ? "Uploading…" : "Bulk upload"}
              </button>
              <button onClick={() => setClipUploadOpen(true)} data-testid="gallery-clip-upload-open" className="btn-ghost inline-flex items-center gap-2">
                <Film size={16} /> Upload clip
              </button>
              <button onClick={() => setFolderEditor({
                title: "", description: "", parent_id: activeFolderId,
                creator_id: "", creator_name: "", tags: [], access_level: "free",
                published: false, cover_picture_id: null, sort_order: folders.length,
              })} data-testid="picture-folder-create" className="btn-ghost inline-flex items-center gap-2">
                <FolderPlus size={16} /> New folder
              </button>
              <button onClick={openNew} data-testid="pic-new-button" className="btn-primary inline-flex items-center gap-2">
                <Plus size={16} /> Add picture
              </button>
            </>
          )}
        </div>
      }>
      <div className="pictures-neon" style={{
        "--gio-primary": "#ff2bd6", "--gio-secondary": "#ff4fd8", "--gio-primary-soft": "rgba(255,43,214,.14)",
        "--gio-border": "rgba(255,43,214,.32)", "--gio-surface": "#0d0617", "--gio-surface-hi": "#1a0b2a",
        "--gio-text": "#fff", "--gio-muted": "#e9c9e5", "--gio-muted-2": "#d0b3ce", "--gio-muted-3": "#a88da7",
      }}>
      <style>{`.pictures-neon{color:#fff;background:linear-gradient(145deg,#050109 0%,#10051b 56%,#1a0827 100%);border:1px solid rgba(255,43,214,.35);border-radius:14px;padding:16px;box-shadow:0 0 20px rgba(255,43,214,.12)}.pictures-neon .gio-input{background:#12091d!important;border-color:rgba(255,43,214,.34)!important;color:#fff!important}.pictures-neon .gio-input:focus{outline:none!important;border-color:#ff4fd8!important;box-shadow:0 0 0 2px rgba(255,43,214,.35)!important}.pictures-neon .btn-primary{background:#ff2bd6!important;color:#fff!important;box-shadow:0 0 14px rgba(255,43,214,.45)}.pictures-neon .btn-ghost{border-color:rgba(255,43,214,.38);color:#ffe2f7}.pictures-neon [data-testid^=pic-card-]{box-shadow:0 0 12px rgba(255,43,214,.12)}.pictures-neon [data-testid^=pic-card-]:hover{box-shadow:0 0 18px rgba(255,43,214,.35)}.pictures-neon input:focus-visible,.pictures-neon select:focus-visible,.pictures-neon button:focus-visible{outline:2px solid #ff4fd8;outline-offset:2px}`}</style>
      {/* Page-level drop zone for drag-and-drop uploads */}
      <div
        onDragOver={onPageDragOver}
        onDragLeave={onPageDragLeave}
        onDrop={onPageDrop}
        className="relative"
        data-testid="pic-page-dropzone">
        {pageDragOver && (
          <div className="fixed inset-0 z-40 flex flex-col items-center justify-center pointer-events-none"
            data-testid="pic-page-drop-overlay"
            style={{ background: "rgba(255,43,214,0.18)", border: "4px dashed var(--gio-primary)" }}>
            <ImageIcon size={64} style={{ color: "var(--gio-primary)" }} />
            <div className="text-[28px] font-heading mt-4" style={{ color: "var(--gio-primary)" }}>Drop to upload pictures</div>
            <div className="text-[14px] mt-2" style={{ color: "var(--gio-text)" }}>
              Drop image files anywhere on this page
            </div>
          </div>
        )}
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          value={search} onChange={(e) => setSearch(e.target.value)}
          data-testid="pic-search"
          placeholder="Search title, description, tags..."
          className="gio-input flex-1 min-w-[200px] text-sm" />
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
          data-testid="pic-filter"
          className="gio-input text-sm" style={{ width: "auto" }}>
          <option value="all">All pictures</option>
          <option value="ready">✅ Ready to post</option>
          <option value="pending">⏳ Pending edit</option>
          <option value="published">Published to members</option>
          <option value="unpublished">Unpublished</option>
        </select>
        <select value={creatorFilter} onChange={e => setCreatorFilter(e.target.value)} data-testid="pic-creator-filter" className="gio-input text-sm">
          <option value="all">All models / creators</option>
          {creatorOptions.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
        <select value={tagFilter} onChange={e => setTagFilter(e.target.value)} data-testid="pic-tag-filter" className="gio-input text-sm">
          <option value="all">All tags</option>
          {tagOptions.map(tag => <option key={tag} value={tag}>{tag}</option>)}
        </select>
        <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)}
          aria-label="Pictures taken on or after" data-testid="pic-date-filter" className="gio-input text-sm" />
        <div className="flex border rounded overflow-hidden" style={{ borderColor: "var(--gio-border)" }}>
          <button onClick={() => setView("grid")} data-testid="pic-view-grid"
            className="p-2 transition"
            style={{ background: view === "grid" ? "var(--gio-primary)" : "transparent", color: view === "grid" ? "#000" : "var(--gio-muted-2)" }}>
            <GridIcon size={14} />
          </button>
          <button onClick={() => setView("table")} data-testid="pic-view-table"
            className="p-2 transition"
            style={{ background: view === "table" ? "var(--gio-primary)" : "transparent", color: view === "table" ? "#000" : "var(--gio-muted-2)" }}>
            <ListIcon size={14} />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4 text-sm" data-testid="picture-breadcrumbs">
        <button onClick={() => setActiveFolderId(null)} className="text-zinc-200 hover:text-white">All pictures</button>
        {breadcrumbs.map(folder => (
          <React.Fragment key={folder.id}>
            <ChevronRight size={14} className="text-zinc-600" />
            <button onClick={() => setActiveFolderId(folder.id)} className="text-zinc-300 hover:text-white">
              {folder.title}
            </button>
          </React.Fragment>
        ))}
        <span className="ml-auto text-xs text-zinc-500" data-testid="picture-folder-count">
          {childFolders.length} folders · {folderPictures.length} pictures
        </span>
        {activeFolderId && (
          <button onClick={() => setFolderEditor(folders.find(folder => folder.id === activeFolderId))}
            className="text-xs text-cyan-200 hover:text-white" data-testid="picture-folder-edit-current">
            Edit folder
          </button>
        )}
      </div>

      {childFolders.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4" data-testid="picture-folder-grid">
          {childFolders.map(folder => {
            const cover = items.find(picture => picture.id === folder.cover_picture_id) || items.find(picture => picture.folder_id === folder.id);
            return (
              <div key={folder.id} className="group relative rounded-lg border border-white/10 bg-white/[0.03] overflow-hidden hover:border-cyan-400/40 transition"
                data-testid={`picture-folder-${folder.id}`}>
                <button onClick={() => setActiveFolderId(folder.id)} className="w-full text-left">
                  <div className="aspect-[16/9] bg-zinc-900 flex items-center justify-center overflow-hidden">
                    {cover ? <PictureThumbnail pic={cover} className="w-full h-full object-cover" /> : <Folder size={28} className="text-cyan-300" />}
                  </div>
                  <div className="p-3 pr-16">
                    <span className="block truncate text-sm font-semibold text-white">{folder.title}</span>
                    <span className="block text-xs text-zinc-500">{folder.picture_count || 0} pictures · {folder.access_level}</span>
                    <span className="block text-[10px] text-zinc-500">{folder.published ? "Published" : "Draft"}{folder.creator_name ? ` · ${folder.creator_name}` : ""}</span>
                  </div>
                </button>
                <div className="absolute top-2 right-2 flex gap-1">
                  <button onClick={() => setFolderEditor(folder)} title="Edit folder" aria-label={`Edit ${folder.title}`} className="bg-black/80 p-1.5 rounded text-zinc-200 hover:text-cyan-200">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => deleteFolder(folder)} title="Delete folder" aria-label={`Delete ${folder.title}`} className="bg-black/80 p-1.5 rounded text-zinc-200 hover:text-red-300">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {uploadProgress && (
        <div className="mb-4 rounded-lg border border-cyan-400/20 bg-cyan-400/5 px-4 py-3" role="status" data-testid="pic-upload-progress">
          <div className="flex justify-between text-xs text-cyan-100 mb-2">
            <span>Uploading pictures</span>
            <span>{uploadProgress.complete}/{uploadProgress.total}{uploadProgress.failed ? ` · ${uploadProgress.failed} failed` : ""}</span>
          </div>
          <progress className="w-full accent-cyan-400" value={uploadProgress.complete} max={uploadProgress.total} />
        </div>
      )}

      {loadError && !loading && (
        <div className="my-6 rounded-lg border border-red-500/30 bg-red-950/30 p-6 text-center" role="alert">
          <p className="text-red-200">{loadError}</p>
          <button onClick={load} className="btn-ghost mt-3">Retry</button>
        </div>
      )}
      {loading && <div className="py-12 text-center text-sm text-zinc-400" data-testid="pic-loading">Loading pictures…</div>}

      {/* Empty state */}
      {!loading && !loadError && filtered.length === 0 && (
        <div className="text-center py-16 border border-dashed rounded-lg" style={{ borderColor: "var(--gio-border)" }} data-testid="pic-empty">
          <ImageIcon size={32} className="mx-auto mb-3" style={{ color: "var(--gio-muted-3)" }} />
          <div className="text-sm" style={{ color: "var(--gio-muted-2)" }}>{search || filter !== "all" ? "No pictures match these filters" : "This folder is empty"}</div>
          {!search && filter === "all" && <button onClick={openNew} className="btn-primary mt-4 inline-flex items-center gap-2"><Plus size={14} /> Add picture</button>}
        </div>
      )}

      {/* Grid view */}
      {view === "grid" && filtered.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="pic-grid">
          {filtered.map((p) => (
            <PictureCard key={p.id} pic={p} onEdit={() => openEdit(p)} onDelete={() => remove(p.id)} onPatch={patch}
              onPreview={() => setPreviewPicture(p)}
              selectMode={selectMode} isSelected={selected.has(p.id)} onToggleSelect={() => toggleSelect(p.id)}
              onTierToggle={(tier) => togglePictureTier(p, tier)}
              onMoveUp={() => movePictureOrder(p.id, -1)} onMoveDown={() => movePictureOrder(p.id, 1)}
              canReorder={!search.trim() && filter === "all" && creatorFilter === "all" && tagFilter === "all" && !dateFilter} />
          ))}
        </div>
      )}

      {/* Table view */}
      {view === "table" && filtered.length > 0 && (
        <div className="border rounded-lg overflow-x-auto" style={{ borderColor: "var(--gio-border)" }} data-testid="pic-table">
          <table className="w-full text-[12px]">
            <thead style={{ background: "var(--gio-surface)" }}>
              <tr>
                <th className="p-2 text-left">Picture</th>
                <th className="p-2 text-left">Member tiers</th>
                <th className="p-2 text-left">Edit</th>
                {PLATFORMS.map(pl => (
                  <React.Fragment key={pl.key}>
                    <th className="p-2 text-left">{pl.label}</th>
                    {pl.priced && <th className="p-2 text-left">${pl.label.split(" ")[0]}</th>}
                  </React.Fragment>
                ))}
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t" style={{ borderColor: "var(--gio-border)" }}>
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <PictureThumbnail pic={p} className="w-16 aspect-video object-cover rounded border" />
                      <div>
                        <div className="font-bold text-[13px]">{p.title || "Untitled"}</div>
                        {p.tags?.length > 0 && <div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>{p.tags.slice(0, 3).join(" · ")}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="p-2"><div className="flex gap-1">
                    {TIERS.map(tier => {
                      const active = (p.visibility || []).includes(tier.store) || (tier.key === "basic" && (p.visibility || []).includes("basic"));
                      const color = tier.key === "free" ? "border-cyan-400 text-cyan-200" : tier.key === "basic" ? "border-emerald-400 text-emerald-200" : "border-amber-400 text-amber-100";
                      return <button key={tier.key} type="button" onClick={() => togglePictureTier(p, tier.key)} data-testid={`pic-table-tier-${tier.key}-${p.id}`}
                        className={`rounded border px-1.5 py-1 text-[9px] ${active ? color : "border-zinc-700 text-zinc-400"}`}>{tier.label}</button>;
                    })}
                  </div></td>
                  <td className="p-2">
                    <select value={p.edit_status} onChange={(e) => patch(p.id, "edit_status", e.target.value)}
                      data-testid={`pic-edit-status-${p.id}`}
                      className="gio-input text-[11px]" style={{ minWidth: 90 }}>
                      {EDIT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </td>
                  {PLATFORMS.map(pl => (
                    <React.Fragment key={pl.key}>
                      <td className="p-2">
                        <select value={p[`${pl.key}_status`] || "none"}
                          onChange={(e) => patch(p.id, `${pl.key}_status`, e.target.value)}
                          data-testid={`pic-${pl.key}-${p.id}`}
                          className="gio-input text-[11px]" style={{ minWidth: 80 }}>
                          {PPV_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </td>
                      {pl.priced && (
                        <td className="p-2">
                          <input type="number" min="0" step="0.5"
                            value={p[`${pl.key}_price`] || 0}
                            onChange={(e) => patch(p.id, `${pl.key}_price`, parseFloat(e.target.value) || 0)}
                            data-testid={`pic-${pl.key}-price-${p.id}`}
                            className="gio-input text-[11px]" style={{ width: 60 }} />
                        </td>
                      )}
                    </React.Fragment>
                  ))}
                  <td className="p-2">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(p)} className="hover:text-[color:var(--gio-primary)]" title="Edit"><Pencil size={12} /></button>
                      <button onClick={() => remove(p.id)} className="hover:text-red-400" title="Delete"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <PictureModal
          pic={editing}
          folderId={activeFolderId}
          folders={folders}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSaved={() => { setOpen(false); setEditing(null); load(); }}
        />
      )}
      {clipUploadOpen && (
        <GalleryClipUploadModal
          folders={folders}
          defaultFolderId={activeFolderId}
          onClose={() => setClipUploadOpen(false)}
          onComplete={load}
        />
      )}
      {previewPicture && (
        <GalleryMediaViewer
          picture={previewPicture}
          onClose={() => setPreviewPicture(null)}
          playbackUrl={`/api/pictures/clips/${previewPicture.id}/playback-token`}
        />
      )}
      {folderEditor && (
        <FolderEditor
          folder={folderEditor}
          folders={folders}
          pictures={items}
          crmContacts={crmContacts}
          onClose={() => setFolderEditor(null)}
          onSaved={async () => { setFolderEditor(null); await load(); }}
        />
      )}
      <CloudinaryPicker
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        multi={true}
        onPickMany={onBulkPicked}
        folderHint="pictures/uploads"
      />
      {bulkEditOpen && (
        <BulkEditModal
          count={selected.size}
          onClose={() => setBulkEditOpen(false)}
          onApply={runBulkEdit}
          crmContacts={crmContacts}
        />
      )}
      </div>
      </div>
    </AppLayout>
  );
}

function cloudinaryThumbnail(url) {
  if (!url) return "";
  if (url.includes("res.cloudinary.com") && url.includes("/upload/")) {
    return url.replace("/upload/", "/upload/f_auto,q_auto,w_640,h_640,c_fill/");
  }
  return url;
}

function PictureThumbnail({ pic, className }) {
  const videoPoster = pic.media_type === "video" && pic.poster_ready
    ? (pic.poster_url || `/api/pictures/clips/${pic.id}/poster`)
    : "";
  const sources = [...new Set([videoPoster, pic.thumbnail_url, cloudinaryThumbnail(pic.image_url), pic.image_url].filter(Boolean))];
  const [sourceIndex, setSourceIndex] = useState(0);
  useEffect(() => setSourceIndex(0), [pic.thumbnail_url, pic.image_url]);
  if (!sources[sourceIndex]) {
    return <div className={`${className || ""} flex flex-col items-center justify-center bg-[#150a22] text-zinc-500`} role="img" aria-label="Image unavailable">
      <ImageIcon size={24} /><span className="mt-1 text-[9px] uppercase tracking-wide">GioPilos</span>
    </div>;
  }
  return <GalleryImage src={sources[sourceIndex]} alt={pic.title || "Picture"} className={className}
    onError={() => setSourceIndex(i => i + 1)} />;
}

/* ── Picture grid card ── */
function PictureCard({ pic, onEdit, onDelete, onPatch, onPreview, selectMode, isSelected, onToggleSelect, onTierToggle, onMoveUp, onMoveDown, canReorder }) {
  const postedCount = PLATFORMS.filter(pl => (pic[`${pl.key}_status`] || "none") === "posted").length;
  return (
    <div className="rounded border overflow-hidden group transition" data-testid={`pic-card-${pic.id}`}
      style={{
        borderColor: isSelected ? "var(--gio-primary)" : "var(--gio-border)",
        background: isSelected ? "var(--gio-primary-soft)" : "var(--gio-surface)",
      }}
      onClick={selectMode ? onToggleSelect : undefined}>
      <div className="relative aspect-square bg-zinc-900" style={{ cursor: selectMode ? "pointer" : "default" }}>
        <PictureThumbnail pic={pic} className="w-full h-full object-cover" />
        {selectMode && (
          <div className="absolute top-1 left-1 z-10">
            <input type="checkbox" checked={!!isSelected} readOnly
              data-testid={`pic-select-${pic.id}`}
              className="w-5 h-5 pointer-events-none" />
          </div>
        )}
        {!selectMode && (
          <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition">
            {pic.media_type === "video" && <button onClick={(e) => { e.stopPropagation(); onPreview(); }} aria-label={`Preview ${pic.title || "clip"}`} title="Preview clip"
              className="bg-black/80 p-1.5 rounded border border-zinc-700 text-white hover:text-cyan-200"><PlayCircle size={13} /></button>}
            <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={!canReorder}
              aria-label={`Move ${pic.title || "picture"} earlier`} title="Move earlier"
              className="bg-black/80 p-1.5 rounded border border-zinc-700 hover:text-cyan-200 disabled:opacity-30 disabled:cursor-not-allowed"><ArrowUp size={11} /></button>
            <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={!canReorder}
              aria-label={`Move ${pic.title || "picture"} later`} title="Move later"
              className="bg-black/80 p-1.5 rounded border border-zinc-700 hover:text-cyan-200 disabled:opacity-30 disabled:cursor-not-allowed"><ArrowDown size={11} /></button>
            <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="bg-black/80 p-1.5 rounded border border-zinc-700 hover:text-[color:var(--gio-primary)]"><Pencil size={11} /></button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="bg-black/80 p-1.5 rounded border border-zinc-700 hover:text-red-400"><Trash2 size={11} /></button>
          </div>
        )}
        <div className="absolute bottom-1 left-1 text-[9px] font-mono-ui px-1.5 py-0.5 rounded uppercase font-bold"
          style={{
            background: pic.edit_status === "edited" ? "rgba(34,197,94,0.85)" : pic.edit_status === "pending" ? "rgba(120,120,120,0.85)" : "rgba(245,158,11,0.85)",
            color: "#000",
          }}>
          {pic.edit_status || "pending"}
        </div>
        {pic.published && <span className="absolute top-1 left-1 rounded bg-emerald-500/90 px-1.5 py-0.5 text-[9px] font-bold uppercase text-black">Published</span>}
        {pic.media_type === "video" && <>
          <span data-testid={`pic-play-badge-${pic.id}`} className="absolute inset-0 flex items-center justify-center pointer-events-none"><PlayCircle size={36} className="fill-black/60 text-white drop-shadow" /></span>
          {Number.isFinite(Number(pic.duration)) && <span data-testid={`pic-duration-${pic.id}`} className="absolute right-1 top-1 inline-flex items-center gap-1 rounded bg-black/80 px-1.5 py-0.5 text-[9px] text-white"><Clock size={10} />{formatDuration(pic.duration)}</span>}
        </>}
        {postedCount > 0 && (
          <div className="absolute bottom-1 right-1 text-[9px] bg-black/80 border border-zinc-700 px-1.5 py-0.5 rounded font-mono-ui">
            {postedCount}/7 posted
          </div>
        )}
      </div>
      <div className="p-2">
        <div className="text-[12px] font-bold truncate" title={pic.title}>{pic.title || "Untitled"}</div>
        <div className="flex gap-1 mt-1.5">
          {TIERS.map(tier => {
            const active = (pic.visibility || []).includes(tier.store) || (tier.key === "basic" && (pic.visibility || []).includes("basic"));
            const palette = tier.key === "free" ? "border-cyan-400/60 text-cyan-200 bg-cyan-500/20" : tier.key === "basic" ? "border-emerald-400/60 text-emerald-200 bg-emerald-500/20" : "border-amber-400/60 text-amber-100 bg-amber-500/20";
            return <button key={tier.key} type="button" onClick={e => { e.stopPropagation(); if (!selectMode) onTierToggle(tier.key); }}
              title={`${active ? "Remove from" : "Assign to"} ${tier.label}`} data-testid={`pic-tier-${tier.key}-${pic.id}`}
              className={`rounded border px-1.5 py-0.5 text-[9px] font-bold ${active ? palette : "border-zinc-700 text-zinc-400"}`}>{tier.label}</button>;
          })}
        </div>
        {pic.tags?.length > 0 && (
          <div className="text-[10px] truncate mt-0.5" style={{ color: "var(--gio-muted-3)" }}>
            {pic.tags.slice(0, 3).join(" · ")}
          </div>
        )}
        <div className="grid grid-cols-7 gap-0.5 mt-2" title="Platform status: green=posted, yellow=presale/scheduled, gray=none">
          {PLATFORMS.map(pl => {
            const s = pic[`${pl.key}_status`] || "none";
            const color = s === "posted" ? "#22c55e"
                        : s === "sale" || s === "live_sub" ? "#14b8a6"
                        : s === "presale" || s === "scheduled" ? "#f59e0b"
                        : "#3f3f46";
            return (
              <button key={pl.key}
                onClick={(e) => { e.stopPropagation(); if (selectMode) return; onPatch(pic.id, `${pl.key}_status`, s === "posted" ? "none" : "posted"); }}
                title={`${pl.label}: ${s}`} data-testid={`pic-card-${pic.id}-${pl.key}-pill`}
                className="h-2 rounded-sm hover:opacity-70"
                style={{ background: color, cursor: selectMode ? "default" : "pointer" }} />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function formatDuration(value) {
  const seconds = Math.max(0, Math.floor(Number(value) || 0));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/* ── Add / Edit modal ── */
function PictureModal({ pic, folderId, folders, onClose, onSaved }) {
  const [form, setForm] = useState(pic || {
    title: "", description: "", tags: [], image_url: "", image_public_id: "",
    taken_at: new Date().toISOString().slice(0, 10), edit_status: "pending",
    promo_notes: "", crew_ids: [], crew_names_cache: [],
    linked_video_id: null, linked_video_title_cache: "", folder_id: folderId, published: false,
  });
  const [busy, setBusy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [crmContacts, setCrmContacts] = useState([]);
  const [videoOptions, setVideoOptions] = useState([]);
  const [videoSearch, setVideoSearch] = useState("");

  // Load CRM contacts + videos once for picker
  React.useEffect(() => {
    api.get("/api/crm/?limit=500").then(r => setCrmContacts(r.data || [])).catch(() => {});
    api.get("/api/videos/?limit=500").then(r => setVideoOptions(r.data || [])).catch(() => {});
  }, []);

  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!form.tags.includes(t)) up("tags", [...form.tags, t]);
    setTagInput("");
  };
  const removeTag = (t) => up("tags", form.tags.filter(x => x !== t));
  const toggleTier = (tier) => {
    const values = tier === "basic" ? ["basic", "members"] : [tier];
    const current = form.visibility || [];
    const enabled = values.some(value => current.includes(value));
    up("visibility", [...current.filter(value => !values.includes(value)), ...(!enabled ? [tier === "basic" ? "members" : tier] : [])]);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!form.image_url) { toast.error("Pick an image first"); return; }
    setBusy(true);
    try {
      if (pic?.id) {
        await api.put(`/api/pictures/${pic.id}`, form);
        toast.success("Updated");
      } else {
        await api.post("/api/pictures/", form);
        toast.success("Created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 backdrop-blur"
      onClick={onClose} data-testid="pic-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-zinc-950 border w-full max-w-2xl max-h-[95vh] overflow-y-auto rounded-t-2xl sm:rounded-xl"
        style={{ borderColor: "var(--gio-border)" }}>
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-zinc-950 z-10" style={{ borderColor: "var(--gio-border)" }}>
          <div>
            <div className="overline text-[color:var(--gio-primary)]">{pic?.id ? "EDIT" : "NEW"} PICTURE</div>
            <h2 className="text-lg font-bold">{pic?.id ? form.title : "Add a picture"}</h2>
          </div>
          <button onClick={onClose} data-testid="pic-modal-close" className="text-zinc-400 hover:text-white"><XIcon size={20} /></button>
        </div>
        <div className="p-4 space-y-4">
          {/* Image picker */}
          <div>
            <div className="overline mb-2">Image *</div>
            {form.image_url ? (
              <div className="relative inline-block">
                <img src={form.image_url} alt="" className="max-h-60 rounded border" style={{ borderColor: "var(--gio-border)" }} />
                <button onClick={() => { up("image_url", ""); up("image_public_id", ""); }}
                  className="absolute top-2 right-2 bg-black/80 border border-zinc-700 p-1 rounded hover:text-red-400"
                  data-testid="pic-modal-image-clear">
                  <XIcon size={12} />
                </button>
              </div>
            ) : (
              <button onClick={() => setShowPicker(true)} data-testid="pic-modal-image-pick"
                className="border-2 border-dashed rounded p-8 w-full hover:border-[color:var(--gio-primary)] hover:text-[color:var(--gio-primary)] transition inline-flex flex-col items-center gap-2"
                style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-2)" }}>
                <Camera size={24} />
                <span className="font-mono-ui text-[11px] uppercase">Choose / Upload image</span>
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="overline mb-1.5">Title *</div>
              <input value={form.title} onChange={(e) => up("title", e.target.value)} data-testid="pic-modal-title"
                placeholder="Athens rooftop set #3" className="gio-input w-full" />
            </div>
            <div>
              <div className="overline mb-1.5">Date taken</div>
              <input type="date" value={form.taken_at} onChange={(e) => up("taken_at", e.target.value)}
                data-testid="pic-modal-date" className="gio-input w-full" />
            </div>
            <div>
              <div className="overline mb-1.5">Edit status</div>
              <select value={form.edit_status} onChange={(e) => up("edit_status", e.target.value)}
                data-testid="pic-modal-edit-status" className="gio-input w-full">
                {EDIT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <div className="overline mb-1.5">Tags</div>
              <div className="flex gap-1">
                <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                  data-testid="pic-modal-tag-input"
                  placeholder="Add tag + Enter" className="gio-input flex-1 text-[12px]" />
                <button onClick={addTag} className="btn-ghost text-[11px]" data-testid="pic-modal-tag-add">+</button>
              </div>
              {form.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {form.tags.map(t => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                      style={{ background: "var(--gio-surface-hi)", border: "1px solid var(--gio-border)" }}>
                      {t} <button onClick={() => removeTag(t)} className="hover:text-red-400">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-xs text-zinc-300">
              Folder
              <select value={form.folder_id || ""} onChange={e => up("folder_id", e.target.value || null)}
                data-testid="pic-modal-folder" className="gio-input w-full mt-1">
                <option value="">Root / Unfiled</option>
                {(folders || []).map(folder => <option key={folder.id} value={folder.id}>{folder.title}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-zinc-300 mt-5 sm:mt-0">
              <input type="checkbox" checked={!!form.published} onChange={e => up("published", e.target.checked)}
                data-testid="pic-modal-published" className="accent-cyan-400" />
              Published to members
            </label>
          </div>
          <div>
            <div className="overline mb-1.5">Member tiers</div>
            <div className="flex flex-wrap gap-2">
              {TIERS.map(tier => {
                const active = (form.visibility || []).includes(tier.store) || (tier.key === "basic" && (form.visibility || []).includes("basic"));
                const tone = tier.key === "free" ? "border-cyan-400 text-cyan-200" : tier.key === "basic" ? "border-emerald-400 text-emerald-200" : "border-amber-400 text-amber-100";
                return <label key={tier.key} className={`flex items-center gap-2 rounded border px-3 py-2 text-xs ${active ? tone : "border-zinc-700 text-zinc-400"}`}>
                  <input type="checkbox" checked={active} onChange={() => toggleTier(tier.key)} className="accent-[#ff2bd6]" />{tier.label}
                </label>;
              })}
            </div>
          </div>
          <div>
            <div className="overline mb-1.5">Description</div>
            <textarea value={form.description} onChange={(e) => up("description", e.target.value)}
              data-testid="pic-modal-description"
              rows={2} placeholder="Optional context, mood, who's in it..." className="gio-input w-full text-[12px]" />
          </div>

          {/* Linked video */}
          <div>
            <div className="overline mb-1.5">🎬 Linked video (optional)</div>
            <input value={videoSearch} onChange={(e) => setVideoSearch(e.target.value)}
              data-testid="pic-modal-video-search"
              placeholder="Type to search videos by title..."
              className="gio-input w-full text-[12px]" />
            {form.linked_video_id && (
              <div className="mt-1.5 text-[11px] flex items-center gap-2">
                <span className="px-2 py-0.5 rounded inline-flex items-center gap-1"
                  style={{ background: "rgba(20,184,166,0.15)", border: "1px solid rgba(20,184,166,0.4)", color: "#5eead4" }}>
                  🎬 {form.linked_video_title_cache || form.linked_video_id}
                  <button onClick={() => { up("linked_video_id", null); up("linked_video_title_cache", ""); }}
                    className="hover:text-red-400" data-testid="pic-modal-video-clear">×</button>
                </span>
              </div>
            )}
            {videoSearch.length >= 2 && (
              <div className="mt-1.5 border rounded max-h-32 overflow-y-auto" style={{ borderColor: "var(--gio-border)" }}>
                {videoOptions
                  .filter(v => (v.title || "").toLowerCase().includes(videoSearch.toLowerCase()))
                  .slice(0, 10)
                  .map(v => (
                    <button key={v.id} onClick={() => {
                        up("linked_video_id", v.id);
                        up("linked_video_title_cache", v.title || "Untitled");
                        setVideoSearch("");
                      }}
                      data-testid={`pic-modal-video-option-${v.id}`}
                      className="block w-full text-left px-3 py-1.5 text-[11px] hover:bg-zinc-900 border-b last:border-0"
                      style={{ borderColor: "var(--gio-border)" }}>
                      🎬 {v.title || "Untitled"}
                      {v.is_studio && <span className="ml-2 text-[9px] text-purple-400">[STUDIO]</span>}
                    </button>
                  ))}
              </div>
            )}
          </div>

          {/* Crew tagging — pulls from CRM */}
          <div>
            <div className="overline mb-1.5">👥 Tag people (from CRM)</div>
            {form.crew_ids?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1.5">
                {form.crew_ids.map((id, i) => (
                  <span key={id} className="text-[11px] px-2 py-0.5 rounded inline-flex items-center gap-1"
                    style={{ background: "var(--gio-surface-hi)", border: "1px solid var(--gio-border)" }}>
                    👤 {form.crew_names_cache[i] || crmContacts.find(c => c.id === id)?.stage_name || id}
                    <button onClick={() => {
                        const newIds = form.crew_ids.filter(x => x !== id);
                        const newNames = form.crew_names_cache.filter((_, j) => j !== i);
                        up("crew_ids", newIds);
                        up("crew_names_cache", newNames);
                      }} className="hover:text-red-400"
                      data-testid={`pic-modal-crew-remove-${id}`}>×</button>
                  </span>
                ))}
              </div>
            )}
            <select onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                if (form.crew_ids.includes(v)) { e.target.value = ""; return; }
                const c = crmContacts.find(x => x.id === v);
                if (!c) return;
                up("crew_ids", [...form.crew_ids, v]);
                up("crew_names_cache", [...form.crew_names_cache, c.stage_name || c.legal_name || c.email || v]);
                e.target.value = "";
              }}
              data-testid="pic-modal-crew-select"
              className="gio-input w-full text-[12px]">
              <option value="">+ Add person from CRM ({crmContacts.length} contacts)</option>
              {crmContacts
                .filter(c => !form.crew_ids.includes(c.id))
                .slice(0, 150)
                .map(c => (
                  <option key={c.id} value={c.id}>
                    {c.stage_name || c.legal_name || c.email || c.id}
                    {c.is_studio ? " [STUDIO]" : ""}
                  </option>
                ))}
            </select>
            <div className="text-[10px] mt-1" style={{ color: "var(--gio-muted-3)" }}>
              Each tagged contact gets an &quot;interactions&quot; entry on their CRM profile when you save.
            </div>
          </div>
          <div>
            <div className="overline mb-1.5">Promo notes (caption ideas)</div>
            <textarea value={form.promo_notes} onChange={(e) => up("promo_notes", e.target.value)}
              data-testid="pic-modal-promo-notes"
              rows={2} placeholder="Caption draft / hashtags / hook ideas..." className="gio-input w-full text-[12px]" />
          </div>

          {/* Content 360° — only when editing existing picture */}
          {pic?.id && <Content360Panel pictureId={pic.id} />}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t sticky bottom-0 bg-zinc-950" style={{ borderColor: "var(--gio-border)" }}>
          <button onClick={onClose} className="btn-ghost" data-testid="pic-modal-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary disabled:opacity-40" data-testid="pic-modal-save">
            {busy ? "Saving…" : pic?.id ? "Save changes" : "Add picture"}
          </button>
        </div>
      </div>
      {showPicker && (
        <CloudinaryPicker
          folder="pictures"
          onPick={(u) => {
            up("image_url", u.secure_url);
            up("image_public_id", u.public_id);
            if (!form.thumbnail_url) up("thumbnail_url", u.secure_url);
            setShowPicker(false);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

function FolderEditor({ folder, folders, pictures, crmContacts, onClose, onSaved }) {
  const [draft, setDraft] = useState({
    title: "", description: "", parent_id: null, creator_id: "", creator_name: "",
    tags: [], access_level: "free", published: false, cover_picture_id: null, sort_order: 0,
    ...folder,
  });
  const [tagsText, setTagsText] = useState((folder?.tags || []).join(", "));
  const [busy, setBusy] = useState(false);
  const update = (field, value) => setDraft(current => ({ ...current, [field]: value }));
  const coverOptions = pictures.filter(picture => picture.folder_id === folder?.id);
  const save = async (event) => {
    event.preventDefault();
    if (!draft.title.trim()) return toast.error("Folder title required");
    const payload = {
      title: draft.title.trim(),
      description: draft.description || "",
      parent_id: draft.parent_id || null,
      creator_id: draft.creator_id || null,
      creator_name: draft.creator_name || "",
      tags: tagsText.split(",").map(tag => tag.trim()).filter(Boolean),
      access_level: draft.access_level,
      published: !!draft.published,
      cover_picture_id: draft.cover_picture_id || null,
      sort_order: Number(draft.sort_order) || 0,
    };
    setBusy(true);
    try {
      if (folder?.id) await api.put(`/api/pictures/folders/${folder.id}`, payload);
      else await api.post("/api/pictures/folders/", payload);
      toast.success(folder?.id ? "Folder updated" : "Folder created");
      await onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Folder could not be saved");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 p-0 sm:p-4 backdrop-blur-sm"
      onClick={onClose} data-testid="picture-folder-editor">
      <form onSubmit={save} onClick={event => event.stopPropagation()}
        className="w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-t-xl sm:rounded-xl border border-white/10 bg-zinc-950 shadow-2xl">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-zinc-950 p-4">
          <div><p className="text-[10px] uppercase tracking-wider text-cyan-300">Picture Gallery</p><h2 className="text-lg font-semibold text-white">{folder?.id ? "Edit folder" : "Create folder"}</h2></div>
          <button type="button" onClick={onClose} aria-label="Close folder editor" className="p-2 text-zinc-400 hover:text-white"><XIcon size={18} /></button>
        </header>
        <div className="grid gap-4 p-4 sm:grid-cols-2">
          <label className="text-xs text-zinc-300">Title
            <input required value={draft.title} onChange={e => update("title", e.target.value)} className="gio-input mt-1 w-full" data-testid="folder-title" />
          </label>
          <label className="text-xs text-zinc-300">Parent folder
            <select value={draft.parent_id || ""} onChange={e => update("parent_id", e.target.value || null)} className="gio-input mt-1 w-full" data-testid="folder-parent">
              <option value="">Root</option>
              {folders.filter(item => item.id !== folder?.id).map(item => <option key={item.id} value={item.id}>{item.title}</option>)}
            </select>
          </label>
          <label className="text-xs text-zinc-300 sm:col-span-2">Description
            <textarea value={draft.description} onChange={e => update("description", e.target.value)} rows={2} className="gio-input mt-1 w-full" data-testid="folder-description" />
          </label>
          <label className="text-xs text-zinc-300">Model / creator
            <select value={draft.creator_id || ""} onChange={e => {
              const contact = crmContacts.find(item => item.id === e.target.value);
              update("creator_id", contact?.id || "");
              update("creator_name", contact?.stage_name || contact?.legal_name || "");
            }} className="gio-input mt-1 w-full" data-testid="folder-creator">
              <option value="">No creator</option>
              {crmContacts.map(contact => <option key={contact.id} value={contact.id}>{contact.stage_name || contact.legal_name || contact.email || contact.id}</option>)}
            </select>
          </label>
          <label className="text-xs text-zinc-300">Access level
            <select value={draft.access_level} onChange={e => update("access_level", e.target.value)} className="gio-input mt-1 w-full" data-testid="folder-access">
              <option value="free">Free</option><option value="basic">Basic</option><option value="vip">VIP</option>
            </select>
          </label>
          <label className="text-xs text-zinc-300">Sort order
            <input type="number" value={draft.sort_order || 0} onChange={e => update("sort_order", Number(e.target.value))}
              className="gio-input mt-1 w-full" data-testid="folder-sort-order" />
          </label>
          <label className="text-xs text-zinc-300 sm:col-span-2">Tags
            <input value={tagsText} onChange={e => setTagsText(e.target.value)} placeholder="travel, behind-the-scenes" className="gio-input mt-1 w-full" data-testid="folder-tags" />
          </label>
          <label className="text-xs text-zinc-300 sm:col-span-2">Cover picture
            <select value={draft.cover_picture_id || ""} onChange={e => update("cover_picture_id", e.target.value || null)} className="gio-input mt-1 w-full" data-testid="folder-cover">
              <option value="">Use first picture</option>
              {coverOptions.map(picture => <option key={picture.id} value={picture.id}>{picture.title || "Untitled picture"}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-200 sm:col-span-2">
            <input type="checkbox" checked={!!draft.published} onChange={e => update("published", e.target.checked)} className="accent-cyan-400" data-testid="folder-published" />
            Publish this folder to members
          </label>
        </div>
        <footer className="sticky bottom-0 flex justify-end gap-2 border-t border-white/10 bg-zinc-950 p-4">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary disabled:opacity-50" data-testid="folder-save">{busy ? "Saving…" : "Save folder"}</button>
        </footer>
      </form>
    </div>
  );
}


/* ── Bulk Edit modal — apply the same patch to many pictures at once ──
   Each field has a checkbox toggle so only the enabled rows go into the request.
   Uses sticky header + scrollable body + sticky footer for tall screens. */
function BulkEditModal({ count, onClose, onApply, crmContacts = [] }) {
  // ---- Basic fields ----
  const [useEditStatus, setUseEditStatus] = useState(false);
  const [editStatus, setEditStatus] = useState("edited");

  const [useTitle, setUseTitle] = useState(false);
  const [title, setTitle] = useState("");

  const [useDescription, setUseDescription] = useState(false);
  const [description, setDescription] = useState("");

  const [usePromoNotes, setUsePromoNotes] = useState(false);
  const [promoNotes, setPromoNotes] = useState("");

  const [useTakenAt, setUseTakenAt] = useState(false);
  const [takenAt, setTakenAt] = useState("");

  // ---- Tags ----
  const [useAddTags, setUseAddTags] = useState(false);
  const [addTags, setAddTags] = useState("");
  const [useRemoveTags, setUseRemoveTags] = useState(false);
  const [removeTags, setRemoveTags] = useState("");

  // ---- Crew (CRM multi-select) ----
  const [useAddCrew, setUseAddCrew] = useState(false);
  const [addCrewIds, setAddCrewIds] = useState([]); // array of CRM ids
  const [useRemoveCrew, setUseRemoveCrew] = useState(false);
  const [removeCrewIds, setRemoveCrewIds] = useState([]);

  // ---- Studio ----
  const [useIsStudio, setUseIsStudio] = useState(false);
  const [isStudio, setIsStudio] = useState(true);
  const [useStudioCrm, setUseStudioCrm] = useState(false);
  const [studioCrmId, setStudioCrmId] = useState("");
  const [useStudioPublished, setUseStudioPublished] = useState(false);
  const [studioPublished, setStudioPublished] = useState(true);

  // ---- Per-platform: status + price + posted_at ----
  // { of_vip: { status: "posted", price: "", posted_at: "" }, ... }
  const [platformCfg, setPlatformCfg] = useState({});
  const togglePlatform = (key) => setPlatformCfg(ps => {
    const next = { ...ps };
    if (key in next) delete next[key];
    else next[key] = { status: "posted", price: "", posted_at: "" };
    return next;
  });
  const setPlatformField = (key, field, value) => setPlatformCfg(ps => ({
    ...ps,
    [key]: { ...ps[key], [field]: value },
  }));

  const build = () => {
    const set = {};
    const add_tags = [];
    const remove_tags = [];
    const add_crew_ids = [];
    const remove_crew_ids = [];

    if (useEditStatus) set.edit_status = editStatus;
    if (useTitle) set.title = title;
    if (useDescription) set.description = description;
    if (usePromoNotes) set.promo_notes = promoNotes;
    if (useTakenAt && takenAt) set.taken_at = takenAt;

    if (useIsStudio) set.is_studio = isStudio;
    if (useStudioCrm) {
      set.studio_crm_id = studioCrmId || null;
      if (studioCrmId) {
        const c = crmContacts.find(x => x.id === studioCrmId);
        set.studio_name_cache = c ? (c.stage_name || c.legal_name || c.email || "") : "";
      } else {
        set.studio_name_cache = "";
      }
    }
    if (useStudioPublished) {
      set.studio_published = studioPublished;
      set.studio_published_at = studioPublished ? new Date().toISOString().slice(0, 10) : null;
    }

    // Per-platform: emit status, price and posted_at when populated
    Object.entries(platformCfg).forEach(([k, cfg]) => {
      if (cfg.status) set[`${k}_status`] = cfg.status;
      if (cfg.price !== "" && cfg.price !== null && cfg.price !== undefined) {
        const p = Number(cfg.price);
        if (!Number.isNaN(p)) set[`${k}_price`] = p;
      }
      if (cfg.posted_at) set[`${k}_posted_at`] = cfg.posted_at;
    });

    if (useAddTags && addTags.trim()) {
      addTags.split(",").map(t => t.trim()).filter(Boolean).forEach(t => add_tags.push(t));
    }
    if (useRemoveTags && removeTags.trim()) {
      removeTags.split(",").map(t => t.trim()).filter(Boolean).forEach(t => remove_tags.push(t));
    }
    if (useAddCrew) add_crew_ids.push(...addCrewIds);
    if (useRemoveCrew) remove_crew_ids.push(...removeCrewIds);

    return { set, add_tags, remove_tags, add_crew_ids, remove_crew_ids };
  };

  const summary = () => {
    const parts = [];
    if (useEditStatus) parts.push(`edit → ${editStatus}`);
    if (useTitle) parts.push(`title → "${title.slice(0, 40)}${title.length > 40 ? "…" : ""}"`);
    if (useDescription) parts.push(`description → "${description.slice(0, 40)}${description.length > 40 ? "…" : ""}"`);
    if (usePromoNotes) parts.push(`promo_notes → "${promoNotes.slice(0, 40)}${promoNotes.length > 40 ? "…" : ""}"`);
    if (useTakenAt && takenAt) parts.push(`taken_at → ${takenAt}`);
    if (useIsStudio) parts.push(`is_studio → ${isStudio}`);
    if (useStudioCrm) {
      const c = crmContacts.find(x => x.id === studioCrmId);
      const name = c ? (c.stage_name || c.legal_name || c.email || studioCrmId) : "(cleared)";
      parts.push(`studio → ${name}`);
    }
    if (useStudioPublished) parts.push(`studio_published → ${studioPublished}`);
    Object.entries(platformCfg).forEach(([k, cfg]) => {
      const subs = [`status=${cfg.status}`];
      if (cfg.price !== "") subs.push(`$${cfg.price}`);
      if (cfg.posted_at) subs.push(cfg.posted_at);
      parts.push(`${k} → ${subs.join(" · ")}`);
    });
    if (useAddTags && addTags.trim()) parts.push(`+tags: ${addTags}`);
    if (useRemoveTags && removeTags.trim()) parts.push(`-tags: ${removeTags}`);
    if (useAddCrew && addCrewIds.length) parts.push(`+crew: ${addCrewIds.length}`);
    if (useRemoveCrew && removeCrewIds.length) parts.push(`-crew: ${removeCrewIds.length}`);
    return parts;
  };
  const changes = summary();

  const submit = () => {
    if (changes.length === 0) return toast.error("Enable at least one field to apply");
    if (!window.confirm(`Apply ${changes.length} change${changes.length === 1 ? "" : "s"} to ${count} picture${count === 1 ? "" : "s"}?`)) return;
    onApply(build());
  };

  const contactLabel = (c) => c.stage_name || c.legal_name || c.email || c.id;

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose} data-testid="pic-bulk-edit-modal">
      <div className="bg-zinc-950 border border-zinc-800 max-w-3xl w-full my-8 rounded-xl flex flex-col max-h-[calc(100vh-4rem)]"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-5 border-b border-zinc-900 flex items-center justify-between shrink-0">
          <div>
            <div className="overline text-[color:var(--gio-primary)]">BULK EDIT · {count} PICTURE{count === 1 ? "" : "S"}</div>
            <h2 className="font-heading text-xl mt-1">Edit everything at once</h2>
            <div className="text-[11px] mt-1 italic" style={{ color: "var(--gio-muted-3)" }}>
              Only checked fields get applied. Untouched fields keep their per-picture values.
            </div>
          </div>
          <button onClick={onClose} data-testid="pic-bulk-edit-close" className="text-zinc-500 hover:text-white"><XIcon size={20} /></button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5 overflow-y-auto flex-1">

          {/* ── SECTION: Basic ── */}
          <Section title="Basic">
            <BulkRow enabled={useEditStatus} onToggle={setUseEditStatus} label="Edit status" testid="bulk-row-edit-status">
              <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)}
                className="gio-input" data-testid="pic-bulk-edit-status">
                {EDIT_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </BulkRow>

            <BulkRow enabled={useTitle} onToggle={setUseTitle} label="Title (replace)" testid="bulk-row-title">
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Same title for every selected picture" className="gio-input"
                data-testid="pic-bulk-title" />
            </BulkRow>

            <BulkRow enabled={useDescription} onToggle={setUseDescription} label="Description (replace)" testid="bulk-row-description">
              <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="Overwrites description on every selected picture" rows={2}
                className="gio-input" data-testid="pic-bulk-description" />
            </BulkRow>

            <BulkRow enabled={usePromoNotes} onToggle={setUsePromoNotes} label="Promo notes (replace)" testid="bulk-row-promo-notes">
              <textarea value={promoNotes} onChange={(e) => setPromoNotes(e.target.value)}
                placeholder="Internal notes / caption drafts" rows={2}
                className="gio-input" data-testid="pic-bulk-promo-notes" />
            </BulkRow>

            <BulkRow enabled={useTakenAt} onToggle={setUseTakenAt} label="Taken at (shoot date)" testid="bulk-row-taken-at">
              <input type="date" value={takenAt} onChange={(e) => setTakenAt(e.target.value)}
                className="gio-input" data-testid="pic-bulk-taken-at" />
            </BulkRow>
          </Section>

          {/* ── SECTION: Tags ── */}
          <Section title="Tags">
            <BulkRow enabled={useAddTags} onToggle={setUseAddTags} label="Add tags (comma-separated)" testid="bulk-row-add-tags">
              <input value={addTags} onChange={(e) => setAddTags(e.target.value)}
                placeholder="e.g. athens, promo, bts" className="gio-input"
                data-testid="pic-bulk-add-tags" />
            </BulkRow>
            <BulkRow enabled={useRemoveTags} onToggle={setUseRemoveTags} label="Remove tags (comma-separated)" testid="bulk-row-remove-tags">
              <input value={removeTags} onChange={(e) => setRemoveTags(e.target.value)}
                placeholder="e.g. draft, unused" className="gio-input"
                data-testid="pic-bulk-remove-tags" />
            </BulkRow>
          </Section>

          {/* ── SECTION: Crew ── */}
          <Section title="Crew (CRM contacts)">
            <BulkRow enabled={useAddCrew} onToggle={setUseAddCrew} label="Add crew" testid="bulk-row-add-crew">
              <CrewPicker contacts={crmContacts} selected={addCrewIds} onChange={setAddCrewIds}
                labelFn={contactLabel} testidPrefix="pic-bulk-add-crew" />
            </BulkRow>
            <BulkRow enabled={useRemoveCrew} onToggle={setUseRemoveCrew} label="Remove crew" testid="bulk-row-remove-crew">
              <CrewPicker contacts={crmContacts} selected={removeCrewIds} onChange={setRemoveCrewIds}
                labelFn={contactLabel} testidPrefix="pic-bulk-remove-crew" />
            </BulkRow>
          </Section>

          {/* ── SECTION: Studio ── */}
          <Section title="Studio linkage">
            <BulkRow enabled={useIsStudio} onToggle={setUseIsStudio} label="Is studio shoot?" testid="bulk-row-is-studio">
              <select value={isStudio ? "yes" : "no"}
                onChange={(e) => setIsStudio(e.target.value === "yes")}
                className="gio-input" data-testid="pic-bulk-is-studio">
                <option value="yes">Yes — studio shoot</option>
                <option value="no">No — personal</option>
              </select>
            </BulkRow>
            <BulkRow enabled={useStudioCrm} onToggle={setUseStudioCrm} label="Studio (CRM contact)" testid="bulk-row-studio-crm">
              <select value={studioCrmId} onChange={(e) => setStudioCrmId(e.target.value)}
                className="gio-input" data-testid="pic-bulk-studio-crm">
                <option value="">— clear studio —</option>
                {crmContacts.map(c => (
                  <option key={c.id} value={c.id}>{contactLabel(c)}</option>
                ))}
              </select>
            </BulkRow>
            <BulkRow enabled={useStudioPublished} onToggle={setUseStudioPublished} label="Studio published" testid="bulk-row-studio">
              <select value={studioPublished ? "yes" : "no"}
                onChange={(e) => setStudioPublished(e.target.value === "yes")}
                className="gio-input" data-testid="pic-bulk-studio-published">
                <option value="yes">Published (today)</option>
                <option value="no">Not published (clear)</option>
              </select>
            </BulkRow>
          </Section>

          {/* ── SECTION: Platforms — status + price + posted_at ── */}
          <Section title="Platform status · price · posted date">
            <div className="text-[11px] italic mb-2" style={{ color: "var(--gio-muted-3)" }}>
              Enable a platform, then set any of: status, price (leave blank to keep), posted date.
            </div>
            <div className="grid grid-cols-1 gap-2" data-testid="bulk-row-platforms">
              {PLATFORMS.map(pl => {
                const active = pl.key in platformCfg;
                const cfg = platformCfg[pl.key] || {};
                return (
                  <div key={pl.key} className="rounded border p-2.5 transition"
                    data-testid={`pic-bulk-plat-${pl.key}`}
                    style={{
                      borderColor: active ? "var(--gio-primary)" : "var(--gio-border)",
                      background: active ? "var(--gio-primary-soft)" : "transparent",
                    }}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={active} onChange={() => togglePlatform(pl.key)}
                        data-testid={`pic-bulk-plat-toggle-${pl.key}`} />
                      <span className="text-[13px] font-heading flex-1"
                        style={{ color: active ? "var(--gio-primary)" : "var(--gio-muted-2)" }}>{pl.label}</span>
                    </div>
                    {active && (
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <div>
                          <div className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: "var(--gio-muted-3)" }}>Status</div>
                          <select value={cfg.status || "posted"}
                            onChange={(e) => setPlatformField(pl.key, "status", e.target.value)}
                            data-testid={`pic-bulk-plat-status-${pl.key}`}
                            className="gio-input text-[11px] py-0.5 px-1">
                            {PPV_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        {pl.priced && (
                          <div>
                            <div className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: "var(--gio-muted-3)" }}>Price $</div>
                            <input type="number" min="0" step="0.01"
                              value={cfg.price ?? ""}
                              onChange={(e) => setPlatformField(pl.key, "price", e.target.value)}
                              placeholder="skip"
                              className="gio-input text-[11px] py-0.5 px-1"
                              data-testid={`pic-bulk-plat-price-${pl.key}`} />
                          </div>
                        )}
                        <div>
                          <div className="text-[9px] uppercase tracking-wider mb-0.5" style={{ color: "var(--gio-muted-3)" }}>Posted at</div>
                          <input type="date"
                            value={cfg.posted_at || ""}
                            onChange={(e) => setPlatformField(pl.key, "posted_at", e.target.value)}
                            className="gio-input text-[11px] py-0.5 px-1"
                            data-testid={`pic-bulk-plat-date-${pl.key}`} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>

          {/* Live summary */}
          {changes.length > 0 && (
            <div className="rounded border p-3" data-testid="pic-bulk-summary"
              style={{ borderColor: "var(--gio-primary)", background: "var(--gio-primary-soft)" }}>
              <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: "var(--gio-primary)" }}>
                Preview · {changes.length} change{changes.length === 1 ? "" : "s"}
              </div>
              <ul className="text-[11px] space-y-0.5" style={{ color: "var(--gio-text)" }}>
                {changes.map((c, i) => <li key={i}>· {c}</li>)}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-zinc-900 flex gap-3 shrink-0">
          <button onClick={submit} disabled={changes.length === 0}
            data-testid="pic-bulk-apply"
            className="btn-primary flex-1 disabled:opacity-40 disabled:cursor-not-allowed">
            Apply to {count} picture{count === 1 ? "" : "s"}
          </button>
          <button onClick={onClose} data-testid="pic-bulk-cancel" className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded border p-3 space-y-3" style={{ borderColor: "var(--gio-border)" }}>
      <div className="text-[10px] font-bold uppercase tracking-wider"
        style={{ color: "var(--gio-primary)" }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function CrewPicker({ contacts, selected, onChange, labelFn, testidPrefix }) {
  const [q, setQ] = useState("");
  const available = contacts.filter(c => !selected.includes(c.id));
  const filtered = q.trim()
    ? available.filter(c => labelFn(c).toLowerCase().includes(q.toLowerCase()))
    : available;
  return (
    <div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5" data-testid={`${testidPrefix}-chips`}>
          {selected.map(id => {
            const c = contacts.find(x => x.id === id);
            return (
              <span key={id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
                data-testid={`${testidPrefix}-chip-${id}`}
                style={{ background: "var(--gio-primary)", color: "#000" }}>
                {c ? labelFn(c) : id}
                <button onClick={() => onChange(selected.filter(x => x !== id))}
                  data-testid={`${testidPrefix}-chip-remove-${id}`}
                  className="opacity-80 hover:opacity-100">×</button>
              </span>
            );
          })}
        </div>
      )}
      <select value=""
        onChange={(e) => { if (e.target.value) { onChange([...selected, e.target.value]); e.target.value = ""; } }}
        className="gio-input text-[11px]"
        data-testid={`${testidPrefix}-select`}>
        <option value="">+ Add contact ({filtered.length} available)</option>
        {filtered.slice(0, 200).map(c => (
          <option key={c.id} value={c.id}>{labelFn(c)}</option>
        ))}
      </select>
      {q && filtered.length === 0 && (
        <div className="text-[10px] italic mt-1" style={{ color: "var(--gio-muted-3)" }}>No matches</div>
      )}
    </div>
  );
}

function BulkRow({ enabled, onToggle, label, testid, children }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)}
        data-testid={`${testid}-toggle`} />
      <div className="flex-1">
        <div className="text-[10px] font-bold uppercase tracking-wider mb-1"
          style={{ color: enabled ? "var(--gio-primary)" : "var(--gio-muted-3)" }}>{label}</div>
        <div style={{ opacity: enabled ? 1 : 0.5, pointerEvents: enabled ? "auto" : "none" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

